import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import _ from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';

import {
    Text,
    StyledCard,
    StyledCardContent,
    BackIcon,
    OutlinedButton,
    ContainedButton,
} from '../../index';

import { topUpUploadSlip } from '../../../services/provider';

import { alertError, alertSuccess } from '../../../redux/stateActions';

const STATUS = Object.freeze({
    CREATED: 'CREATED',
    SENT: 'SENT',
    AWAIT: 'AWAIT',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
});

const InvoiceCard = ({ data, refetch, handleOnClick, handleOnCancel }) => {
    return (
        <StyledCard>
            <StyledCardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    rowGap: '15px',
                }}
            >
                <Text $fontSize="18px" $bold>
                    Awaiting Payment
                </Text>
                {data.map((item) => (
                    <PendingCard
                        key={item.id}
                        data={item}
                        onClick={() => {
                            handleOnClick(item.id);
                        }}
                        onCancel={() => {
                            handleOnCancel(item.id);
                        }}
                        refetch={refetch}
                    />
                ))}
            </StyledCardContent>
        </StyledCard>
    );
};

InvoiceCard.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    refetch: PropTypes.func,
    handleOnClick: PropTypes.func,
    handleOnCancel: PropTypes.func,
};

export default InvoiceCard;

const PendingCard = ({ data, onClick, refetch, onCancel }) => {
    const dispatch = useDispatch();

    const inputRef = useRef(null);

    const handleClickUpload = () => {
        inputRef.current.click();
    };

    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState([]);

    const onFileChange = async (event) => {
        if (event.target.files.length === 0) return;

        const newFile = event.target.files[0];
        event.target.value = null;

        if (!['image/png', 'image/jpeg', 'application/pdf'].includes(newFile.type)) {
            return dispatch(alertError('invalid file type.'));
        }

        const newFiles = [...files];
        newFiles.push(newFile);
        setFiles(newFiles);
    };

    const removeFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            let formData = new FormData();
            formData.append('topUpID', data.id);

            files.forEach((file) => {
                formData.append('files', file);
            });

            const res = await topUpUploadSlip(formData);

            if (res.status === 200) {
                setFiles([]);
                dispatch(alertSuccess(`Files uploaded`));
                refetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Cannot upload files: ${error.message}`));
        }
    };

    const handleViewFile = (url) => {
        window.open(url, '_blank');
    };

    return (
        <StyledAccordion>
            <AccordionSummary
                expandIcon={
                    <div style={{ transform: 'rotate(270deg)' }}>
                        <BackIcon />
                    </div>
                }
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                        <Text onClick={onClick} style={{ width: '130px' }}>
                            {data.number}
                        </Text>
                        <div
                            style={{
                                transform: 'rotate(180deg)',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <BackIcon height="9px" width="6px" />
                        </div>
                        {data.status === STATUS.CREATED && (
                            <Text onClick={onCancel} $color="pink" $fontSize="14px">
                                Cancel
                            </Text>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                        <Text
                            $fontSize="12px"
                            $color={
                                data.status === STATUS.SENT
                                    ? 'blue6'
                                    : data.status === STATUS.AWAIT
                                    ? 'green4'
                                    : 'gray3'
                            }
                        >
                            {data.status === STATUS.SENT
                                ? 'Send Payment Slip'
                                : data.status === STATUS.AWAIT
                                ? 'Awaiting Payment Confirmation'
                                : 'Upload payment slip'}
                        </Text>
                        <Text $color="gray5" $fontSize="12px">
                            |
                        </Text>
                        <Text $color="gray4" $fontSize="12px">
                            Last updated: {dayjs(data.updatedAt).format('DD MMM YYYY')}
                        </Text>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                {data.status === STATUS.CREATED && (
                    <div style={{ width: '100%' }}>
                        {!_.isEmpty(files) && (
                            <div
                                style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}
                            >
                                {files.map((item, index) => (
                                    <div
                                        key={`file-name-${index}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Text $fontSize="14px">{item.name}</Text>
                                        <Text
                                            $color="gray3"
                                            onClick={() => removeFile(index)}
                                            disabled={loading}
                                        >
                                            Remove
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: _.isEmpty(files) ? 'center' : 'flex-end',
                                columnGap: '10px',
                                marginTop: '20px',
                            }}
                        >
                            <input
                                ref={inputRef}
                                id="upload-file"
                                name="filefield"
                                type="file"
                                accept=".jpeg,.jpg,.png,.pdf"
                                onChange={onFileChange}
                                style={{ display: 'none' }}
                            />

                            <OutlinedButton
                                style={{
                                    height: '46px',
                                    width: _.isEmpty(files) ? '210px' : '157px',
                                    border: '1px dashed #789EFF',
                                    borderRadius: '12px',
                                }}
                                onClick={handleClickUpload}
                                disabled={loading}
                            >
                                + Add Image
                            </OutlinedButton>
                            {!_.isEmpty(files) && (
                                <ContainedButton
                                    style={{
                                        height: '46px',
                                        width: '210px',
                                        borderRadius: '12px',
                                    }}
                                    onClick={handleSubmit}
                                    loading={loading}
                                >
                                    Submit
                                </ContainedButton>
                            )}
                        </div>
                    </div>
                )}
                {data.status === STATUS.AWAIT && (
                    <div style={{ width: '100%' }}>
                        {data.slip.map((item) => (
                            <Text
                                key={`await-file-${item.id}`}
                                $fontSize="14px"
                                $color="gray2"
                                onClick={() => handleViewFile(item.url)}
                            >
                                {item.name}
                            </Text>
                        ))}
                    </div>
                )}
            </AccordionDetails>
        </StyledAccordion>
    );
};

PendingCard.propTypes = {
    data: PropTypes.object.isRequired,
    refetch: PropTypes.func,
    onClick: PropTypes.func,
    onCancel: PropTypes.func,
};

const StyledAccordion = withStyles((theme) => ({
    root: {
        border: `1px solid ${theme.palette.common.gray4}`,
        borderRadius: '12px !important',
        boxShadow: 'none',
        backgroundColor: 'transparent',

        '&::before': {
            backgroundColor: 'transparent',
        },
    },
}))(Accordion);
